import { NumberField as NumberFieldPrimitive } from '@base-ui/react/number-field'
import { cn } from '@eigtech/ui/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'
import { MinusIcon, MoveHorizontalIcon, PlusIcon } from 'lucide-react'
import * as React from 'react'

type NumberInputContextType = {
  id: string
}

const NumberInputContext = React.createContext<NumberInputContextType | null>(null)

const inputVariants = cva(
  [
    'border-input flex w-20 select-none items-center justify-center border text-center transition-colors disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:ring-ring/30 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px]',
    'aria-invalid:border-destructive/60 aria-invalid:ring-destructive/10 dark:aria-invalid:border-destructive dark:aria-invalid:ring-destructive/20',
  ],
  {
    variants: {
      size: {
        lg: 'h-10 text-base',
        md: 'h-9 text-sm',
        sm: 'h-8 text-sm',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const buttonVariants = cva(
  [
    'focus-visible:outline-hidden text-foreground border-input inline-flex cursor-pointer items-center justify-center border',
    'focus-visible:ring-ring/30 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px]',
    'hover:bg-muted',
    'ring-offset-background whitespace-nowrap text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-60 [&_svg]:shrink-0',
  ],
  {
    variants: {
      size: {
        lg: 'size-10 [&_svg:not([class*=size-])]:size-4',
        md: 'size-9 [&_svg:not([class*=size-])]:size-3.5',
        sm: 'size-8 [&_svg:not([class*=size-])]:size-3',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const useNumberInput = () => {
  const context = React.useContext(NumberInputContext)

  if (!context) {
    throw new Error('useNumberInput must be used within a NumberInput')
  }

  return context
}

function NumberInput({
  id,
  className,
  children,
  size = 'md',
  ...props
}: React.ComponentProps<typeof NumberFieldPrimitive.Root> & VariantProps<typeof inputVariants>) {
  let fieldId = React.useId()

  if (id) {
    fieldId = id
  }

  return (
    <NumberInputContext.Provider value={{ id: fieldId }}>
      <NumberFieldPrimitive.Root
        className={cn('flex flex-col items-start gap-1', className)}
        data-slot="number-field"
        id={fieldId}
        {...props}
      >
        {children}
        <NumberFieldPrimitive.Group className="shadow-xs text-foreground flex rounded-md shadow-black/5 transition-shadow">
          <NumberFieldPrimitive.Decrement
            className={cn(buttonVariants({ size }), 'rounded-s-md border-e-0')}
            data-slot="number-field-decrement"
          >
            <MinusIcon />
          </NumberFieldPrimitive.Decrement>

          <NumberFieldPrimitive.Input
            className={inputVariants({ size })}
            data-slot="number-field-input"
          />

          <NumberFieldPrimitive.Increment
            className={cn(buttonVariants({ size }), 'rounded-e-md border-s-0')}
            data-slot="number-field-increment"
          >
            <PlusIcon />
          </NumberFieldPrimitive.Increment>
        </NumberFieldPrimitive.Group>
      </NumberFieldPrimitive.Root>
    </NumberInputContext.Provider>
  )
}

function NumberInputScrubArea({
  className,
  children,
  ...props
}: React.ComponentProps<typeof NumberFieldPrimitive.ScrubArea>) {
  const { id: fieldId } = useNumberInput()

  return (
    <NumberFieldPrimitive.ScrubArea
      className={cn('cursor-ew-resize', className)}
      data-slot="number-field-scrub-area"
      {...props}
    >
      <label
        className="text-foreground cursor-ew-resize text-sm font-medium"
        data-slot="number-field-label"
        htmlFor={fieldId}
      >
        {children}
      </label>
      <NumberFieldPrimitive.ScrubAreaCursor
        className="drop-shadow-sm filter"
        data-slot="number-field-scrub-area-cursor"
      >
        <MoveHorizontalIcon className="size-4.5" />
      </NumberFieldPrimitive.ScrubAreaCursor>
    </NumberFieldPrimitive.ScrubArea>
  )
}

export { NumberInput, NumberInputScrubArea }
